// アカウントと権限。Figma「確定｜アカウントと権限｜PC」（753:1340）とダイアログ3つ（754:1476 / 754:1506 / 754:1534）に合わせる。
import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest, type TokenGetter } from "../api";
import "./access.css";

type RoleKey = "system_admin" | "operator";

const ROLE_NAME: Record<string, string> = { system_admin: "全社管理者", operator: "運用担当者" };
const ROLE_SCOPE: Record<RoleKey, string> = {
  system_admin: "全社管理画面のすべてを使えます。illume運営画面にもillume管理者として入れます。",
  operator: "売上・経費だけを扱えます。illume運営画面には入れません。",
};
const ROLE_KEYS: RoleKey[] = ["system_admin", "operator"];

interface Member {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  roleKey: string;
  roleName: string;
  lastAuthenticatedAt: string | null;
}

interface Invitation {
  id: string;
  recipient_name: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string;
  accepted_at: string | null;
  role_key: string;
  role_name: string;
  created_by: string;
  accepted_by: string | null;
}

interface HistoryEvent {
  at: string;
  action: string;
  actor: string | null;
  target: string | null;
  details: Record<string, unknown>;
}

const ERROR_TEXT: Record<string, string> = {
  reason_required: "理由を入力してください。",
  self_change: "自分の役割は変更できません。ほかの全社管理者に依頼してください。",
  last_admin: "最後の全社管理者は下げられません。先にほかの人を全社管理者にしてください。",
  member_not_found: "このメンバーは見つかりませんでした。画面を再読み込みしてください。",
  invitation_not_available: "この招待は、すでに使われたか、取り消されています。",
};
const errorText = (reason: unknown) =>
  ERROR_TEXT[reason instanceof Error ? reason.message : ""] ?? "保存できませんでした。時間を置いてからもう一度お試しください。";

const JST = "Asia/Tokyo";
const parts = (value: string, options: Intl.DateTimeFormatOptions) => {
  const date = new Date(value.includes("T") || value.endsWith("Z") ? value : `${value.replace(" ", "T")}Z`);
  return Object.fromEntries(new Intl.DateTimeFormat("ja-JP", { timeZone: JST, ...options }).formatToParts(date).map((part) => [part.type, part.value]));
};
// 例：9月22日 10:15。今日なら「今日 10:15」。
function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const p = parts(value, { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const today = parts(new Date().toISOString(), { month: "numeric", day: "numeric" });
  const day = p.month === today.month && p.day === today.day ? "今日" : `${p.month}月${p.day}日`;
  return `${day} ${p.hour}:${p.minute}`;
}
function formatDate(value: string): string {
  const p = parts(value, { month: "numeric", day: "numeric" });
  return `${p.month}月${p.day}日`;
}

const STATUS_LABEL: Record<Invitation["status"], string> = { pending: "未使用", accepted: "使用済み", revoked: "取り消し", expired: "期限切れ" };

function describe(event: HistoryEvent): string {
  const actor = event.actor ?? "不明";
  const role = ROLE_NAME[String(event.details.roleKey ?? event.details.to ?? "")] ?? String(event.details.roleKey ?? event.details.to ?? "");
  const reason = event.details.reason ? `理由：${String(event.details.reason)}` : "";
  switch (event.action) {
    case "access.invitation.create": return `${actor}が「${event.target ?? "不明"}」を${role}として招待しました。`;
    case "access.invitation.accept": return `${actor}さんが招待を受け取り、${role}になりました。`;
    case "access.invitation.revoke": return `${actor}が「${event.target ?? "不明"}」への招待を取り消しました。`;
    case "access.member.role_change": return `${actor}が${event.target ?? "不明"}さんを${role}にしました。${reason}`;
    case "access.member.remove": return `${actor}が${event.target ?? "不明"}さんのアクセスを外しました。${reason}`;
    case "access.owner_bootstrap": return `${actor}が最初の全社管理者になりました。`;
    default: return `${actor}が権限の設定を変えました。`;
  }
}

export function AccountRoles({ getToken, currentUserId }: { getToken: TokenGetter; currentUserId: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [history, setHistory] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, i, h] = await Promise.all([
        apiRequest<{ rows: Member[] }>(getToken, "/api/access/members"),
        apiRequest<{ rows: Invitation[] }>(getToken, "/api/access/invitations"),
        apiRequest<{ rows: HistoryEvent[] }>(getToken, "/api/access/history"),
      ]);
      setMembers(m.rows); setInvitations(i.rows); setHistory(h.rows);
    } catch {
      setError("アカウントの情報を読み込めませんでした。時間を置いてから再読み込みしてください。");
    } finally {
      setLoading(false);
    }
  }, [getToken]);
  useEffect(() => { void load(); }, [load]);

  const openCount = invitations.filter((row) => row.status === "pending").length;

  return (
    <>
      <header className="page-header">
        <div>
          <h1 tabIndex={-1}>アカウントと権限</h1>
          <p className="muted">全社管理画面に入れる人と、その役割です。全社管理者は、illume運営画面にもillume管理者として入れます。</p>
        </div>
        <div className="page-action"><button className="button button-primary" onClick={() => setInviteOpen(true)}>メンバーを招待</button></div>
      </header>
      {error && <p className="error-banner" role="alert">{error}</p>}

      <section className="panel" aria-labelledby="members-title">
        <div className="panel-head">
          <div><h2 id="members-title">メンバー</h2><p className="muted">役割を変えられるのは全社管理者だけです。</p></div>
          <span className="panel-count">{members.length}人</span>
        </div>
        <div className="table-wrap">
          <table className="access-table">
            <thead><tr><th scope="col">メンバー</th><th scope="col">役割</th><th scope="col">入れる画面</th><th scope="col">最終ログイン</th><th scope="col"><span className="visually-hidden">操作</span></th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="empty-cell">読み込んでいます…</td></tr>}
              {!loading && members.map((member) => (
                <tr key={member.userId}>
                  <td><span className="person"><Avatar name={member.displayName} src={member.avatarUrl} /><strong>{member.displayName}</strong></span></td>
                  <td>{ROLE_NAME[member.roleKey] ?? member.roleName}</td>
                  <td className={member.roleKey === "system_admin" ? undefined : "muted"}>{member.roleKey === "system_admin" ? "全社管理画面とillume運営画面" : "全社管理画面だけ"}</td>
                  <td className="muted">{formatDateTime(member.lastAuthenticatedAt)}</td>
                  <td className="cell-action">
                    {member.userId === currentUserId
                      ? <span className="muted">自分</span>
                      : <button className="button" onClick={() => setEditing(member)}>役割を変更</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="access-pair">
        <section className="panel" aria-labelledby="invitations-title">
          <div className="panel-head">
            <div><h2 id="invitations-title">招待</h2><p className="muted">リンクは発行から7日間、1回だけ使えます。</p></div>
            <span className="panel-count">未使用 {openCount}件</span>
          </div>
          {!loading && invitations.length === 0 ? <p className="empty-text">まだ招待を発行していません。</p> : (
            <div className="table-wrap">
              <table className="access-table">
                <thead><tr><th scope="col">名前メモ</th><th scope="col">役割</th><th scope="col">状態</th><th scope="col">有効期限</th><th scope="col"><span className="visually-hidden">操作</span></th></tr></thead>
                <tbody>
                  {invitations.map((row) => (
                    <tr key={row.id}>
                      <td><strong>{row.recipient_name}</strong></td>
                      <td>{ROLE_NAME[row.role_key] ?? row.role_name}</td>
                      <td><span className={`access-status ${row.status}`}>{STATUS_LABEL[row.status]}</span></td>
                      <td className="muted">
                        {row.status === "pending" && `${formatDateTime(row.expires_at)}まで`}
                        {row.status === "accepted" && `${formatDate(row.accepted_at ?? row.expires_at)}に使用`}
                        {row.status === "revoked" && "取り消し済み"}
                        {row.status === "expired" && `${formatDate(row.expires_at)}まで`}
                      </td>
                      <td className="cell-action">{row.status === "pending" && <RevokeButton id={row.id} name={row.recipient_name} getToken={getToken} onDone={load} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel access-history" aria-labelledby="history-title">
          <div className="panel-head"><div><h2 id="history-title">変更の履歴</h2><p className="muted">新しい順</p></div></div>
          {!loading && history.length === 0 ? <p className="empty-text">まだ変更はありません。</p> : (
            <ol className="history-list">
              {history.map((event, index) => (
                <li key={index}><time dateTime={event.at}>{formatDateTime(event.at)}</time><p>{describe(event)}</p></li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {inviteOpen && <InviteDialog getToken={getToken} onClose={() => { setInviteOpen(false); void load(); }} />}
      {editing && <RoleDialog member={editing} getToken={getToken} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />}
    </>
  );
}

const AVATAR_COLORS = ["#e4eaf4", "#fcf1d7", "#fbe9e6", "#e9e4f4"];
function Avatar({ name, src }: { name: string; src: string | null }) {
  if (src) return <img className="access-avatar" src={src} alt="" width={32} height={32} />;
  let hash = 0;
  for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  return <span className="access-avatar access-avatar-initial" aria-hidden="true" style={{ background: AVATAR_COLORS[hash % AVATAR_COLORS.length] }}>{name.slice(0, 1)}</span>;
}

function RevokeButton({ id, name, getToken, onDone }: { id: string; name: string; getToken: TokenGetter; onDone: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function revoke() {
    if (!confirm(`「${name}」への招待を取り消しますか？`)) return;
    setBusy(true); setError(null);
    try { await apiRequest(getToken, `/api/access/invitations/${id}`, { method: "DELETE" }); await onDone(); }
    catch (reason) { setError(errorText(reason)); }
    finally { setBusy(false); }
  }
  return <><button className="button" onClick={revoke} disabled={busy}>{busy ? "取り消しています…" : "取り消す"}</button>{error && <span className="error-text" role="alert">{error}</span>}</>;
}

function Dialog({ title, lead, onClose, children }: { title: string; lead?: string; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog ref={ref} className="access-dialog" aria-labelledby="access-dialog-title" onClose={onClose}>
      <h2 id="access-dialog-title">{title}</h2>
      {lead && <p className="access-dialog-lead">{lead}</p>}
      {children}
    </dialog>
  );
}

function RoleChoices({ name, value, onChange, extra }: { name: string; value: string; onChange: (next: string) => void; extra?: { value: string; label: string; description: string; danger?: boolean } }) {
  const options = [
    ...ROLE_KEYS.map((key) => ({ value: key, label: ROLE_NAME[key], description: ROLE_SCOPE[key], danger: false })),
    ...(extra ? [extra] : []),
  ];
  return (
    <div className="choice-list">
      {options.map((option) => (
        <label key={option.value} className={`choice${option.danger ? " choice-danger" : ""}`}>
          <input type="radio" name={name} value={option.value} checked={value === option.value} onChange={() => onChange(option.value)} />
          <span><strong>{option.label}</strong><small>{option.description}</small></span>
        </label>
      ))}
    </div>
  );
}

function InviteDialog({ getToken, onClose }: { getToken: TokenGetter; onClose: () => void }) {
  const [role, setRole] = useState<string>("operator");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ inviteUrl: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      setIssued(await apiRequest<{ inviteUrl: string; expiresAt: string }>(getToken, "/api/access/invitations", {
        method: "POST", body: JSON.stringify({ recipientName: note.trim(), roleKey: role, expiresInDays: 7 }),
      }));
    } catch (reason) { setError(errorText(reason)); }
    finally { setBusy(false); }
  }

  if (issued) {
    return (
      <Dialog title="招待リンクを発行しました" lead="相手にこのリンクを送ってください。リンクを開いてLINEでログインすると、役割が付きます。" onClose={onClose}>
        <div className="form-field">
          <label htmlFor="invite-url">招待リンク</label>
          <div className="copy-row">
            <input id="invite-url" className="input" readOnly value={issued.inviteUrl} onFocus={(event) => event.currentTarget.select()} />
            <button type="button" className="button" onClick={async () => { await navigator.clipboard.writeText(issued.inviteUrl); setCopied(true); }}>{copied ? "コピーしました" : "リンクをコピー"}</button>
          </div>
        </div>
        <dl className="summary-list">
          <div><dt>役割</dt><dd>{ROLE_NAME[role]}</dd></div>
          <div><dt>名前メモ</dt><dd>{note.trim()}</dd></div>
          <div><dt>有効期限</dt><dd>{formatDateTime(issued.expiresAt)}まで（1回だけ使えます）</dd></div>
        </dl>
        <p className="access-note"><span className="access-note-label">確認</span>このリンクは今だけ表示します。閉じると、もう一度表示できません。</p>
        <div className="dialog-actions"><button type="button" className="button button-primary" onClick={onClose}>閉じる</button></div>
      </Dialog>
    );
  }
  return (
    <Dialog title="メンバーを招待" onClose={onClose}>
      <form onSubmit={submit} className="dialog-form">
        <fieldset className="form-field"><legend>役割</legend><p>1つ選んでください。</p><RoleChoices name="invite-role" value={role} onChange={setRole} /></fieldset>
        <div className="form-field">
          <label htmlFor="invite-note">名前メモ</label>
          <p id="invite-note-help">相手には表示しません。だれに渡したリンクかを見分けるために使います。</p>
          <input id="invite-note" className="input" value={note} onChange={(event) => setNote(event.target.value)} required maxLength={100} aria-describedby="invite-note-help" placeholder="例：税理士事務所の方" />
        </div>
        <p className="muted small">リンクは発行から7日間、1回だけ使えます。</p>
        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="dialog-actions"><button type="button" className="button" onClick={onClose}>キャンセル</button><button type="submit" className="button button-primary" disabled={busy}>{busy ? "発行しています…" : "招待リンクを発行"}</button></div>
      </form>
    </Dialog>
  );
}

function RoleDialog({ member, getToken, onClose, onSaved }: { member: Member; getToken: TokenGetter; onClose: () => void; onSaved: () => void }) {
  const [next, setNext] = useState<string>(member.roleKey);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rank: Record<string, number> = { system_admin: 2, operator: 1, remove: 0 };
  const lowering = (rank[next] ?? 0) < (rank[member.roleKey] ?? 0);
  const removing = next === "remove";

  async function submit(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      await apiRequest(getToken, `/api/access/members/${member.userId}`, { method: "PATCH", body: JSON.stringify({ roleKey: removing ? null : next, reason }) });
      onSaved();
    } catch (reason) { setError(errorText(reason)); setBusy(false); }
  }
  return (
    <Dialog title="役割を変更" lead={`${member.displayName}さん（今の役割：${ROLE_NAME[member.roleKey] ?? member.roleName}）`} onClose={onClose}>
      <form onSubmit={submit} className="dialog-form">
        <fieldset className="form-field"><legend>変更後の役割</legend>
          <RoleChoices name="member-role" value={next} onChange={setNext} extra={{ value: "remove", label: "アクセスを外す", description: "全社管理画面に入れなくなります。", danger: true }} />
        </fieldset>
        {lowering && (
          <div className="form-field">
            <label htmlFor="role-reason">理由（必須）</label>
            <p id="role-reason-help">役割を下げるときと外すときに入力します。変更の履歴に残ります。</p>
            <input id="role-reason" className="input" value={reason} onChange={(event) => setReason(event.target.value)} required maxLength={200} aria-describedby="role-reason-help" />
          </div>
        )}
        {removing && <p className="access-note access-note-danger"><span className="access-note-label">注意</span>保存した時点から全社管理画面に入れなくなります。</p>}
        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="dialog-actions"><button type="button" className="button" onClick={onClose}>キャンセル</button><button type="submit" className="button button-primary" disabled={busy || next === member.roleKey}>{busy ? "保存しています…" : "変更を保存"}</button></div>
      </form>
    </Dialog>
  );
}
